import { When } from 'react-if';
import cns from 'classnames';
import { useTranslation } from 'next-i18next';
import { PersonStackFillIcon, PlusIcon } from '@zep/icons';
import { SpaceSimpleDto, SubscribePlan } from '@zep/types';
import { Stack } from '@zep/ui';
import { Colors } from '@zep/ui-tokens';
import { cn } from '@zep/utils';
import { SpacePlan, SpacePlanSubscriptionDto } from '../../../../types/SubscriptionType';
import { PlanSpaceGroup } from '../space/organisms';
import { INFINITE_MAX_CCU, SUBSCRIPTION_FEATURES, SUBSCRIPTION_FEATURES_JA } from '../SubscriptionConst';
import { capitalizeFirstLetter } from '../subscriptionUtil';
import S from './AppliedPlan.module.scss';

/**
 * 플랜 적용 기능 목록
 */
export const AppliedPlanList = ({
  space,
  spacePlan,
  subscription
}: {
  space: SpaceSimpleDto;
  spacePlan?: SpacePlan | null;
  subscription?: SpacePlanSubscriptionDto | null;
}) => {
  const {
    t
  } = useTranslation(['subscription']);
  const isJaPlan = space.countryCode === 'JP';
  const renderCurrentPlanLabel = () => <label className={S.label} data-sentry-component="renderCurrentPlanLabel" data-sentry-source-file="AppliedPlanList.tsx">
      {spacePlan?.maxCcu === INFINITE_MAX_CCU ? t('subscription:SpaceDetail.menuManage.currentPlanMaxCcu') : t('subscription:SpaceDetail.menuManage.currentPlanCcu', {
      ccu: String(spacePlan?.maxCcu)
    })}
    </label>;
  const renderFeatureList = () => {
    if (spacePlan?.planType === 'ENTERPRISE') {
      return <EnterpriseFeatureList isJaPlan={isJaPlan} spacePlan={spacePlan} />;
    }
    return <PlanFeatureList isJaPlan={isJaPlan} spacePlan={spacePlan} data-sentry-element="PlanFeatureList" data-sentry-component="renderFeatureList" data-sentry-source-file="AppliedPlanList.tsx" />;
  };
  const shouldRenderNextPlanFeatureList = () => {
    return !(spacePlan?.planType === 'ENTERPRISE' || (spacePlan?.paymentFailCount ?? 0) > 0 || subscription?.requestedAction === 'DOWNGRADE' || subscription?.requestedAction === 'UNSUBSCRIBE');
  };
  return <PlanSpaceGroup title={t('subscription:SpaceDetail.menuManage.currentPlanFeature', {
    defaultValue: '플랜 제공 기능'
  })} data-sentry-element="PlanSpaceGroup" data-sentry-component="AppliedPlanList" data-sentry-source-file="AppliedPlanList.tsx">
      <Stack gap={10} data-sentry-element="Stack" data-sentry-source-file="AppliedPlanList.tsx">
        <Stack direction={'row'} gap={4} style={{
        alignItems: 'center',
        marginBottom: '2px'
      }} data-sentry-element="Stack" data-sentry-source-file="AppliedPlanList.tsx">
          <PersonStackFillIcon width={18} height={18} fill={Colors.Gray600} data-sentry-element="PersonStackFillIcon" data-sentry-source-file="AppliedPlanList.tsx" />
          {renderCurrentPlanLabel()}
        </Stack>
        {renderFeatureList()}
      </Stack>
      {shouldRenderNextPlanFeatureList() && <NextPlanFeatureList isJaPlan={isJaPlan} spacePlan={spacePlan} />}
    </PlanSpaceGroup>;
};
export const EnterpriseFeatureList = ({
  isJaPlan,
  spacePlan
}: {
  isJaPlan: boolean;
  spacePlan: SpacePlan;
}) => {
  const {
    t
  } = useTranslation(['subscription']);
  const features = isJaPlan ? SUBSCRIPTION_FEATURES_JA[spacePlan?.planType.toLowerCase() as SubscribePlan] : SUBSCRIPTION_FEATURES[spacePlan?.planType.toLowerCase() as SubscribePlan];
  return <>
      {features.map(feature => <Stack key={feature.key} direction={'row'} gap={6} style={{
      alignItems: 'center'
    }}>
          {feature.icon({
        width: 16,
        height: 16,
        fill: '#27262EA3'
      })}
          <label className={S.current_plan_features}>{t(feature.key)}</label>
        </Stack>)}
    </>;
};
export const PlanFeatureList = ({
  isJaPlan,
  spacePlan
}: {
  isJaPlan: boolean;
  spacePlan?: SpacePlan | null;
}) => {
  const {
    t
  } = useTranslation(['subscription']);
  const features = isJaPlan ? SUBSCRIPTION_FEATURES_JA[spacePlan?.planType.toLowerCase() as SubscribePlan] : SUBSCRIPTION_FEATURES[spacePlan?.planType.toLowerCase() as SubscribePlan];
  return <>
      {features.map(feature => <Stack key={feature.key} direction={'row'} gap={6} style={{
      alignItems: 'center'
    }}>
          {feature.icon({
        width: 16,
        height: 16,
        fill: '#27262EA3'
      })}
          <label className={S.current_plan_features}>{t(feature.key)}</label>
        </Stack>)}
    </>;
};
const NextPlanFeatureList = ({
  isJaPlan,
  spacePlan
}: {
  isJaPlan: boolean;
  spacePlan?: SpacePlan | null;
}) => {
  const {
    t
  } = useTranslation(['subscription']);
  const nowPlan = spacePlan?.planType.toLowerCase() ?? 'free';
  const nextPlan = nowPlan === 'basic' ? 'pro' : 'enterprise';
  const nextPlanFeatures = isJaPlan ? SUBSCRIPTION_FEATURES_JA[nextPlan as SubscribePlan] : SUBSCRIPTION_FEATURES[nextPlan as SubscribePlan];
  const nowPlanFeatures = isJaPlan ? SUBSCRIPTION_FEATURES_JA[nowPlan as SubscribePlan] : SUBSCRIPTION_FEATURES[nowPlan as SubscribePlan];

  // nextPlanFeatures에서 nowPlanFeatures에 없는 feature를 필터링
  const features = nextPlanFeatures.filter(nextFeature => !nowPlanFeatures.some(nowFeature => nowFeature.key === nextFeature.key));
  return <Stack className={cn(S.next_feature_wrapper)} gap={8} data-sentry-element="Stack" data-sentry-component="NextPlanFeatureList" data-sentry-source-file="AppliedPlanList.tsx">
      <label className={S.pro_plan_features}>
        {t('subscription:SpaceDetail.menuManage.proPlanDescription', {
        plan: capitalizeFirstLetter(nextPlan)
      })}
      </label>
      {features.map(feature => <Stack key={feature.key} direction={'row'} gap={8} style={{
      alignItems: 'center'
    }}>
          {feature.icon({
        width: 14,
        height: 14,
        fill: '#27262EA3'
      })}
          <label className={cns(S.label, S.small)}>{t(feature.key)}</label>
        </Stack>)}
      <When condition={nextPlan === 'enterprise'} data-sentry-element="When" data-sentry-source-file="AppliedPlanList.tsx">
        <Stack key={'enterpriseSelect'} direction={'row'} gap={8} style={{
        alignItems: 'center'
      }} data-sentry-element="Stack" data-sentry-source-file="AppliedPlanList.tsx">
          <PlusIcon width={14} height={14} fill="#27262EA3" data-sentry-element="PlusIcon" data-sentry-source-file="AppliedPlanList.tsx" />
          <label className={cns(S.label, S.small)}>
            {t('subscription:pricing.features.enterpriseSelect')}
          </label>
        </Stack>
      </When>
    </Stack>;
};